import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  Timestamp,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import type { User, Deeplink, PaginatedResult, AdminStats } from '@/types';

const USERS_COLLECTION = 'users';
const USERNAMES_COLLECTION = 'usernames';
const DEEPLINKS_COLLECTION = 'deeplinks';
const PAGES_COLLECTION = 'pages';

const PAGE_SIZE = 20;

// ============== USER MANAGEMENT ==============

export interface ListUsersParams {
  search?: string;
  pageSize?: number;
  lastDoc?: DocumentSnapshot;
}

export interface UpdateUserInput {
  displayName?: string;
  role?: 'user' | 'admin';
  disabled?: boolean;
  dailyDeeplinkQuota?: number;
}

export const listUsers = async (params: ListUsersParams = {}): Promise<PaginatedResult<User>> => {
  const { search, pageSize = PAGE_SIZE, lastDoc } = params;

  // Build base query
  let q = query(
    collection(db, USERS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(pageSize + 1) // Fetch one extra to check if there's more
  );

  if (lastDoc) {
    q = query(
      collection(db, USERS_COLLECTION),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize + 1)
    );
  }

  const snapshot = await getDocs(q);
  let users = snapshot.docs.map((doc) => ({
    ...doc.data(),
    uid: doc.id,
  })) as User[];

  // Client-side search filtering
  if (search) {
    const searchLower = search.toLowerCase();
    users = users.filter(
      (user) =>
        user.email?.toLowerCase().includes(searchLower) ||
        user.displayName?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower)
    );
  }

  // Get total count
  const countSnapshot = await getCountFromServer(collection(db, USERS_COLLECTION));
  const total = countSnapshot.data().count;

  const hasMore = users.length > pageSize;
  if (hasMore) {
    users = users.slice(0, pageSize);
  }

  return {
    items: users,
    total,
    hasMore,
  };
};

export const getUserById = async (uid: string): Promise<User | null> => {
  const docRef = doc(db, USERS_COLLECTION, uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return { ...docSnap.data(), uid: docSnap.id } as User;
};

export const updateUser = async (uid: string, input: UpdateUserInput): Promise<User> => {
  const docRef = doc(db, USERS_COLLECTION, uid);

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.displayName !== undefined) {
    updateData.displayName = input.displayName;
  }

  if (input.role !== undefined) {
    updateData.role = input.role;
  }

  if (input.disabled !== undefined) {
    updateData.disabled = input.disabled;
  }

  if (input.dailyDeeplinkQuota !== undefined) {
    updateData.dailyDeeplinkQuota = input.dailyDeeplinkQuota;
  }

  await updateDoc(docRef, updateData);

  const docSnap = await getDoc(docRef);
  return { ...docSnap.data(), uid: docSnap.id } as User;
};

export const deleteUser = async (uid: string): Promise<void> => {
  // Get user data first to get username
  const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }

  const userData = userDoc.data();
  const username = userData.username?.toLowerCase();

  // Get all user's deeplinks
  const deeplinksQuery = query(
    collection(db, DEEPLINKS_COLLECTION),
    where('userId', '==', uid)
  );
  const deeplinksSnapshot = await getDocs(deeplinksQuery);

  // Get all user's pages
  const pagesQuery = query(
    collection(db, PAGES_COLLECTION),
    where('userId', '==', uid)
  );
  const pagesSnapshot = await getDocs(pagesQuery);

  // Batch delete everything
  const batch = writeBatch(db);

  // Delete user document
  batch.delete(doc(db, USERS_COLLECTION, uid));

  // Delete username reservation
  if (username) {
    batch.delete(doc(db, USERNAMES_COLLECTION, username));
  }

  // Delete all deeplinks
  deeplinksSnapshot.docs.forEach((deeplink) => {
    batch.delete(doc(db, DEEPLINKS_COLLECTION, deeplink.id));
  });

  // Delete all pages
  pagesSnapshot.docs.forEach((page) => {
    batch.delete(doc(db, PAGES_COLLECTION, page.id));
  });

  await batch.commit();
};

// ============== DEEPLINK MANAGEMENT ==============

export interface ListDeeplinksParams {
  search?: string;
  pageSize?: number;
  lastDoc?: DocumentSnapshot;
}

export interface AdminUpdateDeeplinkInput {
  name?: string;
  originalUrl?: string;
}

export interface DeeplinkWithOwner extends Deeplink {
  ownerEmail?: string;
  ownerDisplayName?: string;
}

export const listAllDeeplinks = async (
  params: ListDeeplinksParams = {}
): Promise<PaginatedResult<DeeplinkWithOwner>> => {
  const { search, pageSize = PAGE_SIZE, lastDoc } = params;

  // Build base query
  let q = query(
    collection(db, DEEPLINKS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(pageSize + 1)
  );

  if (lastDoc) {
    q = query(
      collection(db, DEEPLINKS_COLLECTION),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize + 1)
    );
  }

  const snapshot = await getDocs(q);
  let deeplinks = snapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  })) as DeeplinkWithOwner[];

  // Fetch owner info for each deeplink
  const userIds = [...new Set(deeplinks.map((d) => d.userId))];
  const userMap = new Map<string, { email: string; displayName: string }>();

  await Promise.all(
    userIds.map(async (userId) => {
      const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userMap.set(userId, {
          email: userData.email || '',
          displayName: userData.displayName || '',
        });
      }
    })
  );

  deeplinks = deeplinks.map((deeplink) => ({
    ...deeplink,
    ownerEmail: userMap.get(deeplink.userId)?.email,
    ownerDisplayName: userMap.get(deeplink.userId)?.displayName,
  }));

  // Client-side search filtering
  if (search) {
    const searchLower = search.toLowerCase();
    deeplinks = deeplinks.filter(
      (deeplink) =>
        deeplink.name?.toLowerCase().includes(searchLower) ||
        deeplink.code?.toLowerCase().includes(searchLower) ||
        deeplink.originalUrl?.toLowerCase().includes(searchLower) ||
        deeplink.platform?.toLowerCase().includes(searchLower) ||
        deeplink.ownerEmail?.toLowerCase().includes(searchLower)
    );
  }

  // Get total count
  const countSnapshot = await getCountFromServer(collection(db, DEEPLINKS_COLLECTION));
  const total = countSnapshot.data().count;

  const hasMore = deeplinks.length > pageSize;
  if (hasMore) {
    deeplinks = deeplinks.slice(0, pageSize);
  }

  return {
    items: deeplinks,
    total,
    hasMore,
  };
};

export const adminUpdateDeeplink = async (
  id: string,
  input: AdminUpdateDeeplinkInput
): Promise<Deeplink> => {
  const docRef = doc(db, DEEPLINKS_COLLECTION, id);

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.name !== undefined) {
    updateData.name = input.name;
  }

  if (input.originalUrl !== undefined) {
    updateData.originalUrl = input.originalUrl;
  }

  await updateDoc(docRef, updateData);

  const docSnap = await getDoc(docRef);
  return { id: docSnap.id, ...docSnap.data() } as Deeplink;
};

export const adminDeleteDeeplink = async (id: string): Promise<void> => {
  const docRef = doc(db, DEEPLINKS_COLLECTION, id);
  await deleteDoc(docRef);
};

// ============== ADMIN STATS ==============

export const getAdminStats = async (): Promise<AdminStats> => {
  // Get total users count
  const usersCountSnapshot = await getCountFromServer(collection(db, USERS_COLLECTION));
  const totalUsers = usersCountSnapshot.data().count;

  // Get total deeplinks count
  const deeplinksCountSnapshot = await getCountFromServer(collection(db, DEEPLINKS_COLLECTION));
  const totalDeeplinks = deeplinksCountSnapshot.data().count;

  // Get today's date range
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = Timestamp.fromDate(today);

  // Count users created today
  const usersTodayQuery = query(
    collection(db, USERS_COLLECTION),
    where('createdAt', '>=', todayTimestamp)
  );
  const usersTodaySnapshot = await getCountFromServer(usersTodayQuery);
  const usersToday = usersTodaySnapshot.data().count;

  // Count deeplinks created today
  const deeplinksTodayQuery = query(
    collection(db, DEEPLINKS_COLLECTION),
    where('createdAt', '>=', todayTimestamp)
  );
  const deeplinksTodaySnapshot = await getCountFromServer(deeplinksTodayQuery);
  const deeplinksToday = deeplinksTodaySnapshot.data().count;

  return {
    totalUsers,
    totalDeeplinks,
    usersToday,
    deeplinksToday,
  };
};

// ============== WHITELIST MANAGEMENT ==============

const SETTINGS_COLLECTION = 'settings';
const WHITELIST_DOC = 'whitelist';

const normalizeDomain = (domain: string): string => {
  return domain.toLowerCase().replace(/^www\./, '').trim();
};

export const getWhitelist = async (): Promise<string[]> => {
  const docRef = doc(db, SETTINGS_COLLECTION, WHITELIST_DOC);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return [];
  }

  return (docSnap.data().domains as string[]) || [];
};

export const addWhitelistDomain = async (domain: string, _adminUid: string): Promise<void> => {
  const normalized = normalizeDomain(domain);
  if (!normalized) throw new Error('Invalid domain');

  const docRef = doc(db, SETTINGS_COLLECTION, WHITELIST_DOC);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    await setDoc(docRef, { domains: [normalized], updatedAt: serverTimestamp() });
  } else {
    await updateDoc(docRef, {
      domains: arrayUnion(normalized),
      updatedAt: serverTimestamp(),
    });
  }
};

export const removeWhitelistDomain = async (domain: string, _adminUid: string): Promise<void> => {
  const normalized = normalizeDomain(domain);
  const docRef = doc(db, SETTINGS_COLLECTION, WHITELIST_DOC);

  await updateDoc(docRef, {
    domains: arrayRemove(normalized),
    updatedAt: serverTimestamp(),
  });
};
